import { DEFAULT_SAMPLERS, DEFAULT_SCHEDULERS } from '../defaults'
import type { AppProfile, ModelLists, ServerDefinition, ServerRuntime } from '../types'

type ServerPreferences = {
  order: string[]
  enabled: Record<string, boolean>
}

const personalKey = (identity: string) => `mugen2.servers.personal.${identity || 'local'}`
const temporaryKey = (identity: string) => `mugen2.servers.temporary.${identity || 'local'}`
const preferencesKey = (identity: string) => `mugen2.servers.preferences.${identity || 'local'}`
const modelCacheKey = (identity: string) => `mugen2.servers.models.${identity || 'local'}`

type CachedServerModels = {
  url: string
  savedAt: number
  models: ModelLists
}

const parseJson = <T>(raw: string | null, fallback: T): T => {
  try {
    return raw ? JSON.parse(raw) as T : fallback
  } catch {
    return fallback
  }
}

const normalizeServer = (
  value: unknown,
  source: ServerDefinition['source'],
  index: number,
): ServerDefinition | null => {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const id = typeof record.id === 'string' && record.id.trim()
    ? record.id.trim()
    : `${source}-${index}-${crypto.randomUUID()}`
  const name = typeof record.name === 'string' && record.name.trim()
    ? record.name.trim()
    : source === 'company' ? `Studio ${index + 1}` : `Server ${index + 1}`
  const url = typeof record.url === 'string' ? record.url.trim().replace(/\/+$/, '') : ''
  return { id, name, url, source, enabled: record.enabled !== false }
}

const normalizeList = (value: unknown, source: ServerDefinition['source']): ServerDefinition[] => {
  if (!Array.isArray(value)) return []
  return value
    .map((item, index) => normalizeServer(item, source, index))
    .filter((item): item is ServerDefinition => Boolean(item))
}

const readPreferences = (identity: string): ServerPreferences => {
  const raw = parseJson<Partial<ServerPreferences>>(localStorage.getItem(preferencesKey(identity)), {})
  return {
    order: Array.isArray(raw.order) ? raw.order.filter((id): id is string => typeof id === 'string') : [],
    enabled: raw.enabled && typeof raw.enabled === 'object' ? raw.enabled as Record<string, boolean> : {},
  }
}

export async function loadServerRegistry(identity: string): Promise<ServerDefinition[]> {
  let company: ServerDefinition[] = []
  try {
    const response = await fetch('/config/servers.json', { cache: 'no-store' })
    if (response.ok) {
      const payload = await response.json() as { servers?: unknown }
      company = normalizeList(payload.servers, 'company')
    }
  } catch {
    // Personal and temporary servers remain usable when the central config is unavailable.
  }

  const personal = normalizeList(
    parseJson<unknown>(localStorage.getItem(personalKey(identity)), []),
    'personal',
  )
  const temporary = normalizeList(
    parseJson<unknown>(sessionStorage.getItem(temporaryKey(identity)), []),
    'temporary',
  )
  const preferences = readPreferences(identity)
  const seen = new Set<string>()
  const combined = [...company, ...personal, ...temporary].filter((server) => {
    if (seen.has(server.id)) return false
    seen.add(server.id)
    return true
  }).map((server) => ({
    ...server,
    enabled: preferences.enabled[server.id] ?? server.enabled,
  }))
  const defaultOrder = new Map(combined.map((server, index) => [server.id, index]))
  const savedOrder = new Map(preferences.order.map((id, index) => [id, index]))
  return combined.sort((a, b) =>
    (savedOrder.get(a.id) ?? preferences.order.length + (defaultOrder.get(a.id) ?? 0))
    - (savedOrder.get(b.id) ?? preferences.order.length + (defaultOrder.get(b.id) ?? 0)),
  )
}

export function persistServerRegistry(identity: string, servers: ServerDefinition[]): void {
  const serializable = (server: ServerDefinition) => ({
    id: server.id,
    name: server.name,
    url: server.url,
    enabled: server.enabled,
  })
  localStorage.setItem(personalKey(identity), JSON.stringify(
    servers.filter((server) => server.source === 'personal').map(serializable),
  ))
  sessionStorage.setItem(temporaryKey(identity), JSON.stringify(
    servers.filter((server) => server.source === 'temporary').map(serializable),
  ))
  localStorage.setItem(preferencesKey(identity), JSON.stringify({
    order: servers.map((server) => server.id),
    enabled: Object.fromEntries(servers.map((server) => [server.id, server.enabled])),
  } satisfies ServerPreferences))
}

export function createServer(source: 'personal' | 'temporary'): ServerDefinition {
  return {
    id: crypto.randomUUID(),
    name: source === 'temporary' ? 'Temporary RunPod' : 'Local ComfyUI',
    url: source === 'temporary' ? '' : 'http://127.0.0.1:8188',
    source,
    enabled: true,
  }
}

const stringList = (value: unknown): string[] =>
  Array.isArray(value)
    ? [...new Set(value.filter((item): item is string => typeof item === 'string' && Boolean(item.trim())))]
    : []

const normalizeCachedModels = (value: unknown): ModelLists | null => {
  if (!value || typeof value !== 'object') return null
  const record = value as Record<string, unknown>
  const lora = stringList(record.lora).filter((item) => item !== 'None')
  const samplers = stringList(record.samplers)
  const schedulers = stringList(record.schedulers)
  return {
    unet: stringList(record.unet),
    clip: stringList(record.clip),
    vae: stringList(record.vae),
    clipVision: stringList(record.clipVision),
    lora: ['None', ...lora],
    rife: stringList(record.rife),
    gimm: stringList(record.gimm),
    samplers: samplers.length ? samplers : DEFAULT_SAMPLERS,
    schedulers: schedulers.length ? schedulers : DEFAULT_SCHEDULERS,
    upscale: stringList(record.upscale),
    nodes: stringList(record.nodes),
  }
}

export function readServerModelCache(
  identity: string,
  servers: ServerDefinition[],
): Record<string, ModelLists> {
  const raw = parseJson<Record<string, Partial<CachedServerModels>>>(
    localStorage.getItem(modelCacheKey(identity)),
    {},
  )
  const result: Record<string, ModelLists> = {}
  servers.forEach((server) => {
    const cached = raw[server.id]
    if (!cached || cached.url !== server.url) return
    const models = normalizeCachedModels(cached.models)
    if (models) result[server.id] = models
  })
  return result
}

export function writeServerModelCache(
  identity: string,
  server: ServerDefinition,
  models: ModelLists,
): void {
  try {
    const current = parseJson<Record<string, CachedServerModels>>(
      localStorage.getItem(modelCacheKey(identity)),
      {},
    )
    current[server.id] = { url: server.url, savedAt: Date.now(), models }
    localStorage.setItem(modelCacheKey(identity), JSON.stringify(current))
  } catch {
    // A full or unavailable browser storage must never make a live connection fail.
  }
}

export function deleteServerModelCache(identity: string, serverId: string): void {
  try {
    const current = parseJson<Record<string, CachedServerModels>>(
      localStorage.getItem(modelCacheKey(identity)),
      {},
    )
    if (!(serverId in current)) return
    delete current[serverId]
    localStorage.setItem(modelCacheKey(identity), JSON.stringify(current))
  } catch {
    // Cache cleanup is best effort.
  }
}

export function retryableServers(
  servers: ServerDefinition[],
  runtimes: Record<string, ServerRuntime>,
): ServerDefinition[] {
  return servers.filter((server) => {
    if (!server.enabled || !server.url.trim()) return false
    const status = runtimes[server.id]?.status
    return status !== 'idle' && status !== 'busy' && status !== 'connecting'
  })
}

const merged = (lists: ModelLists[], key: keyof ModelLists): string[] => {
  const result = [...new Set(lists.flatMap((list) => list[key] ?? []))]
    .filter((item) => item !== 'None')
    .sort((a, b) => a.localeCompare(b))
  return key === 'lora' ? ['None', ...result] : result
}

export function mergeModelLists(lists: ModelLists[]): ModelLists {
  if (!lists.length) {
    return {
      unet: [], clip: [], vae: [], clipVision: [], lora: ['None'], rife: [],
      samplers: DEFAULT_SAMPLERS, schedulers: DEFAULT_SCHEDULERS, upscale: [], nodes: [],
    }
  }
  return {
    unet: merged(lists, 'unet'),
    clip: merged(lists, 'clip'),
    vae: merged(lists, 'vae'),
    clipVision: merged(lists, 'clipVision'),
    lora: merged(lists, 'lora'),
    rife: merged(lists, 'rife'),
    gimm: merged(lists, 'gimm'),
    samplers: merged(lists, 'samplers'),
    schedulers: merged(lists, 'schedulers'),
    upscale: merged(lists, 'upscale'),
    nodes: merged(lists, 'nodes'),
  }
}

const isRealLora = (name: string): boolean => Boolean(name && name.trim().toLowerCase() !== 'none')

export function usesRifeBridge(profile: AppProfile): boolean {
  if (!profile.gs.use_rife_bridge || profile.mode !== 'multi' || profile.segments.length < 2) return false
  return profile.segments.at(-1)?.node_type === 'sviflf'
}

export function serverCompatibilityErrors(profile: AppProfile, models: ModelLists): string[] {
  const errors: string[] = []
  const { gs } = profile
  const requireOption = (label: string, value: string, options: string[]) => {
    if (!value || !options.includes(value)) errors.push(`${label}: ${value || '未指定'}`)
  }
  requireOption('High Noise Model', gs.unet_high, models.unet)
  requireOption('Low Noise Model', gs.unet_low, models.unet)
  requireOption('Text Encoder', gs.clip, models.clip)
  requireOption('VAE', gs.vae, models.vae)
  requireOption('CLIP Vision', gs.clip_vision, models.clipVision)
  requireOption('Sampler', gs.sampler, models.samplers)
  requireOption('Scheduler', gs.scheduler, models.schedulers)

  const segments = profile.mode === 'simple' ? [profile.simple_segment] : profile.segments
  const referencedLoras = [gs.accel_lora]
  if (profile.mode === 'multi' && segments.some((segment) => segment.node_type === 'svi' || segment.node_type === 'sviflf')) {
    referencedLoras.push(gs.svi_lora_high, gs.svi_lora_low)
  }
  if (segments.some((segment) => segment.lora_mode === 'global')) {
    referencedLoras.push(...gs.global_lora_high.map((lora) => lora.name))
    referencedLoras.push(...gs.global_lora_low.map((lora) => lora.name))
  }
  segments.filter((segment) => segment.lora_mode === 'custom').forEach((segment) => {
    referencedLoras.push(...segment.lora_high.map((lora) => lora.name))
    referencedLoras.push(...segment.lora_low.map((lora) => lora.name))
  })
  referencedLoras.filter(isRealLora).forEach((name) => {
    if (!models.lora.includes(name)) errors.push(`LoRA: ${name}`)
  })
  if (usesRifeBridge(profile)) requireOption('RIFE Model', gs.rife_model, models.rife)
  return [...new Set(errors)]
}

export function distributeServers(
  candidates: Array<{ server: ServerDefinition; runtime: ServerRuntime }>,
  count: number,
): ServerDefinition[] {
  const assigned = new Map<string, number>()
  return Array.from({ length: Math.max(0, count) }, () => {
    const selected = [...candidates].sort((a, b) => {
      const aLoad = a.runtime.running + a.runtime.pending + (assigned.get(a.server.id) ?? 0)
      const bLoad = b.runtime.running + b.runtime.pending + (assigned.get(b.server.id) ?? 0)
      if (aLoad !== bLoad) return aLoad - bLoad
      return candidates.indexOf(a) - candidates.indexOf(b)
    })[0]
    if (!selected) throw new Error('Compatible Server がありません。')
    assigned.set(selected.server.id, (assigned.get(selected.server.id) ?? 0) + 1)
    return selected.server
  })
}
